import { Injectable, EventEmitter } from '@angular/core';
import { Observable ,  Subject ,  BehaviorSubject } from 'rxjs';
import { HttpClient, HttpResponse, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import * as _ from 'lodash';
import * as moment from 'moment';

import { BaseHttpService } from '../base/_base-http.service';
import { Config } from '../config/env.config';
import { AuthService } from '../auth/auth.service';
import { getAssetThumbnail } from '../digital-assets/digital-asset-helpers';
import { BrandService } from '../brand/brand.service';
import { SessionService } from '../session/session.service';

import { UiModel } from './search.ui.model';
import { SearchModel } from './search.model';
import { StatusModel } from './status.model';
import { MY_PRODUCTS, DIGITAL_ASSETS, PADB, MY_FITMENTS, RECEIVERS } from './search-service-types';
import {
  VEHICLE_FILTER_TYPE, LIST_FILTER_TYPE,
  SIMPLE_FILTER_TYPE, VCDB_FILTER_TYPE,
  PART_ATTRIBUTES_FILTER_TYPE, ARRAY_FILTER_TYPE,
  PADB_FILTER_TYPE, CUSTOM_FILTER_TYPE,
} from './active-filters/active-filter-types';
import { YEAR_START_TYPE, YEAR_END_TYPE } from './advanced-search/filter-types';
import {
  getFiltersGroupsQueryString, getVcdbFiltersGroupsQueryString,
  getPartAttributesFiltersQueryString, getUserAttributesFiltersQueryString,
} from './search-helpers';
import { PreferencesService } from '../preferences/preferences.service';
import { ConvertTZToUTCPipe } from '../pipes/convert-tz-to-utc.pipe';
import {catchError} from 'rxjs/operators';


@Injectable()
export class SearchService extends BaseHttpService {
  selectedItems: Array<any> = [];
  selectAllChecked: Array<any> = []; // {page:number, status:number|undefined}
  searchResultsSubject: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>([]);
  totalRecords: number = 0;
  statuses: Array<StatusModel> = [];
  allSelected: boolean = false;
  searchId: number; // partsImportId or partsExportId

  uiModel: UiModel = {
    isSearchFormOpened: false,
  };

  toggleFormEvent: EventEmitter<Boolean> = new EventEmitter();

  // extendable fields:
  statusLookup: Array<any> = [];
  searchModel: SearchModel = new SearchModel();
  serviceType: string = '';
  idField: string = '';
  timezone: any = moment.tz.guess();

  constructor(
    protected http: HttpClient,
    protected authService: AuthService,
    protected brandService: BrandService,
    protected sessionService: SessionService,
    protected preferencesService: PreferencesService,
    protected convertTZToUTCPipe: ConvertTZToUTCPipe,
  ) {
    super(http, authService, brandService, sessionService);
  }

  get searchResults(): Array<any> {
    return this.searchResultsSubject.getValue();
  }

  set searchResults(data: Array<any>) {
    this.searchResultsSubject.next(data);
  }

  get listFilterId(): string {
    const list = this.searchModel.filters.find((item) => _.get(item, 'type') === LIST_FILTER_TYPE);
    return list ? list._id : '';
  }

  get selectedIds(): Array<any> {
    return _.map(this.selectedItems, this.idField);
  }

  get vehicleFilters(): Array<any> {
    return _.filter(this.searchModel.filters, (item: any) => _.get(item, 'type') === VEHICLE_FILTER_TYPE);
  }

  get partAttributesFilters(): Array<any> {
    return _.filter(this.searchModel.filters, (item: any) => _.get(item, 'type') === PART_ATTRIBUTES_FILTER_TYPE);
  }

  get simpleFilters(): Array<any> {
    return _.filter(this.searchModel.filters, (item: any) => _.get(item, 'type') === SIMPLE_FILTER_TYPE);
  }

  get arrayFilters(): Array<any> {
    return _.filter(this.searchModel.filters, (item: any) => _.get(item, 'type') === ARRAY_FILTER_TYPE);
  }

  get padbAttributesFilters(): Array<any> {
    return _.filter(this.searchModel.filters, (item: any) => _.get(item, 'type') === PADB_FILTER_TYPE);
  }

  get customAttributesFilters(): Array<any> {
    return _.filter(this.searchModel.filters, (item: any) => _.get(item, 'type') === CUSTOM_FILTER_TYPE);
  }

  get vcdbFilters(): Array<any> {
    return _.filter(this.searchModel.filters, (item: any) => _.get(item, 'type') === VCDB_FILTER_TYPE);
  }

  runSearch(brandId: string, searchModel: SearchModel,  ...args: any[]): Observable<any> {

    const subject = new Subject();
    this.preferencesService.getPreferences()
    .subscribe((preferences: any) => {
      this.timezone = preferences.timezone;

      this.searchModel = searchModel;
      const searchUri: string = this.getSearchURI(brandId, searchModel);
      const searchQueryString: string = this.getQueryString(searchModel);

      this.http.get(`${Config.API}${searchUri}?${searchQueryString}`)
      .pipe(catchError(this.handleError.bind(this)))
      .subscribe( res => {
        if (typeof res === 'string') {
          subject.error(res);
          return;
        }
        this.handleSearchRes(res);
        subject.next(res);
        subject.complete();
      });

    });
    return subject;
  }

  handleSearchRes(res: any) {
    this.updateData(res);
  }

  getSearchIds(brandId: string, searchModel: SearchModel ): Observable<any> {
    this.searchModel = searchModel;
    const searchUri: string = this.getSearchURI(brandId, searchModel) + '/ids';
    const searchQueryString: string = this.getQueryString(searchModel);

    return this.http.get(`${Config.API}${searchUri}?${searchQueryString}`)

        .pipe(catchError(this.handleError.bind(this)));
  }

  updateData(data: any) {
    this.searchResults = data.data;
    this.totalRecords = data.total;
    this.searchModel.currentPage = data.current_page;
    this.searchModel.lastPage = data.last_page;
    this.searchModel.total = data.total;
    this.searchModel.searchId = _.get(data, 'id', null);

    if (_.includes([DIGITAL_ASSETS, MY_PRODUCTS, MY_FITMENTS, RECEIVERS], this.serviceType)) {
      if (this.serviceType === DIGITAL_ASSETS) {
        this.setStatuses(_.filter(data.assigned, (status: any) => {
          // except unassigned & not uploaded status
          return status.id !== 2;
        }));
      } else {
        this.setStatuses(data.statuses);
      }

      this.searchResults.map( (item: any) => {
        const selectedItem = _.find(this.selectedItems, [this.idField, item[this.idField]]);
        if (selectedItem) {
          selectedItem.status = item.status;
        }
        item.selected = (this.allSelected || selectedItem);

        if (this.serviceType === DIGITAL_ASSETS) {
          item.thumbnail = getAssetThumbnail(item, 'small');
          // set status for correct displaying of dots
          // and statuses on details page
          // status = [
          // 0 - not uploaded
          // 1 - not assigned
          // 2 - assigned
          // ]
          switch (item.assigned) {
            case 0: // not assigned
              item.status = 1;
              break;
            case 1: // assigned
              item.status = 2;
              break;
            case 3: // not uploaded
              item.status = 0;
              break;
          }
          if (item.file_size) {
            item.filesize = item.file_size;
          }
        } else {
          item.thumbnail = this.getItemPhotoUrl(item.itemId, this.serviceType);
        }
        item.thumbnail = item.thumbnail + '?noCache=' + Date.now();
      });
    }
  }

  setStatuses(statuses: Array<any> = []) {
    const modStatuses = [...statuses];
    _.map(modStatuses, (status: any) => {
      status.name = _.get(_.find(this.statusLookup, ['id', status.id]), 'status');
    });
    this.statuses = modStatuses;
  }

  getItemPhotoUrl(id: string, serviceType: string): string {
    switch (serviceType) {
      case MY_PRODUCTS:
        return `${Config.BACKEND}parts/assets/item/${id}/100`;
      default:
        return '';
    }
  }

  getQueryString(searchModel: SearchModel, process: boolean= false): string {
    const status: string = (searchModel.statusModel) ? searchModel.statusModel.id.toString() : '';
    const sortCol: string = (searchModel.sortModel) ? searchModel.sortModel.column : '';
    const sortDir: string = (searchModel.sortModel) ? searchModel.sortModel.sort : '';
    const searchId: string = (searchModel.searchId) ? searchModel.searchId.toString() : '';

    let queryString = '';
    if (process) {
      queryString =
        'filter[searchValue]=' + encodeURIComponent(searchModel.searchValue.trim()) +
        '&filter[status]=' + status +
        '&list=' + this.listFilterId;

      if (searchId) {
          queryString += '&filter[searchId]=' + searchId;
      }

    } else {
      queryString =
          'page=' + searchModel.currentPage +
          '&itemsPerPage=' + searchModel.itemsPerPage +
          '&search=' + encodeURIComponent(searchModel.searchValue.trim()) +
          '&list=' + this.listFilterId +
          '&status=' + status +
          '&sortCol=' + sortCol +
          '&sortDir=' + sortDir +
          '&columns=' + this.getColumnsQueryString(searchModel.columns);
    }
    queryString +=
      this.getSimpleFiltersQueryString() +
      this.getArrayFiltersQueryString() +
      this.getFiltersQueryString() +
      this.getVcdbFiltersQueryString() +
      this.getPartAttributesFiltersQueryString() +
      this.getPadbFiltersQueryString() +
      this.getCustomFiltersQueryString();
    return queryString;
  }

  getArrayFiltersQueryString() {
    const groups = _.groupBy(this.arrayFilters, '_id'); // group by filter id

    const queryStrings = _.map(groups, (groupFilters: any, filterName: string) => {
      return _.reduce(_.uniqBy(groupFilters, 'title'), (queryString: string, filter: any, index: number) => {
        return queryString + `&filter[${encodeURIComponent(filterName)}][${index}]=${encodeURIComponent(filter.title)}`;
      }, '');
    });

    return _.join(queryStrings, '&');
  }

  getSimpleFiltersQueryString() {
    return _.reduce(this.simpleFilters, (queryString: string, simpleFilter: any) => {
      return queryString + `&filter[${simpleFilter._id}]=${simpleFilter.data}`;
    }, '');
  }

  getSearchURI(brandId: string, searchModel: SearchModel, ...args: any[]): string {
    return '';
  }

  // form
  toggleForm(toggle: boolean): void {
    this.uiModel.isSearchFormOpened = toggle;
    this.toggleFormEvent.emit(this.uiModel.isSearchFormOpened);
  }

  // select checkbox
  selectAll(selected: boolean): void {
    if (this.searchModel.selectAll === 'in-list') {
      this.allSelected = true;
      this.searchResults.map((item: any) => {
        if (!item.uncheckable) {
          item.selected = selected;
        }
      });
    } else {
      this.allSelected = false;

      if (selected) {
        this.pushSelectAllChecked(this.searchModel.currentPage, _.get(this.searchModel.statusModel, 'id'));
      } else {
        this.pullSelectAllChecked(this.searchModel.currentPage, _.get(this.searchModel.statusModel, 'id'));
      }

      this.searchResults.map((item: any) => {
        if (!item.uncheckable) {
          item.selected = selected;
          if (selected) {
            this.pushSelectedItem(item);
          } else {
            this.pullSelectedItem(item);
          }
        }
      });
    }
  }

  deselectAll() {
    this.allSelected = false;
    this.selectAllChecked = [];
    this.selectedItems = [];
    this.searchResults.map((item: any) => {
      if (!item.uncheckable) {
        item.selected = false;
      }
    });
  }

  pushSelectedItem(item: any) {
    const predicate = [this.idField, item[this.idField]];
    // @ts-ignore
      if (_.findIndex(this.selectedItems, predicate) === -1) {
      this.selectedItems.push(item);
    }
  }

  pushSelectedItems(items: Array<any>) {
    if (!items) { return; }
    _.each(items, (item: any) => this.pushSelectedItem(item));
  }

  pullSelectedItem(item: any) {
    _.pullAllBy(this.selectedItems, [item], this.idField);
    this.pullSelectAllChecked(this.searchModel.currentPage, _.get(this.searchModel.statusModel, 'id'));
  }

  pushSelectAllChecked(page: number, status: any) {
    const alreadySelected = _.some(this.selectAllChecked, (item: any) => {
      return item.page === page && item.status === status;
    });
    if (alreadySelected) { return; }
    this.selectAllChecked.push({page, status});
  }

  pullSelectAllChecked(page: number, status: any) {
    // @ts-ignore
      _.remove(this.selectAllChecked, {page, status});
  }

  selectedItemsCount() {
    if (this.allSelected) {
      return this.totalRecords;
    } else {
      return this.selectedItems.length;
    }
  }

  clearSelectedItems() {
    _.remove(this.selectedItems);
    this.searchResults.map((item: any) => {
      if (item.selected) {
        item.selected = false;
      }
    });
    if (this.isCurrentPageSelectAllChecked()) {
      this.pullSelectAllChecked(this.searchModel.currentPage, _.get(this.searchModel.statusModel, 'id'));
    }
  }

  /*
   *  Is the current page and current status select all checked?
   */
  isCurrentPageSelectAllChecked(): boolean {
    return _.some(this.selectAllChecked, (item: any) => {
      return item.page === this.searchModel.currentPage &&
      item.status === _.get(this.searchModel.statusModel, 'id');
    });
  }

  getSelectedStatusCounts() {
    if (!this.allSelected) {
      const statusCounts = _.countBy(this.selectedItems, (item: any) => {
        return item.status;
      });

      const results: Array<any> = _.map(statusCounts, (count, id) => {
        return {statusId: id, count: count};
      });

      return results;

    } else {
      const statusCounts: any = _.filter(this.statuses, (status) => status.statusAmount > 0);
      const results: Array<any> = _.map(statusCounts, (statusCount: any) => {
        return {statusId: statusCount.id, count: statusCount.statusAmount};
      });

      return results;
    }
  }

  getStatusName(id: number): string {
    // @ts-ignore
      const status = _.find(this.statusLookup, {id: id});
    if (status) {
      return status.status;
    } else {
      return '';
    }
  }

  getFiltersQueryString(): string {
    return getFiltersGroupsQueryString(this.vehicleFilters);
  }

  getVcdbFiltersQueryString(): string {
    return getVcdbFiltersGroupsQueryString(this.vcdbFilters);
  }

  getPartAttributesFiltersQueryString(): string {
    const filters: any[] = _.map(this.partAttributesFilters, (filter: any) => {
      const handledFilter = {...filter};
      if (filter.data.dateFromValue) {
        // add UTC value
        const date = `${handledFilter.data.dateFromValue} 00:00:00`;
        const dateTZ = this.convertTZToUTCPipe.transform(date, this.timezone);
        handledFilter.data.dateFromValueUTC = moment(dateTZ).format('YYYY-MM-DD HH:mm:ss');
      }
      if (handledFilter.data.dateToValue) {
        // add UTC value
        const date = `${handledFilter.data.dateToValue} 23:59:59`;
        const dateTZ = this.convertTZToUTCPipe.transform(date, this.timezone);
        handledFilter.data.dateToValueUTC = moment(dateTZ).format('YYYY-MM-DD HH:mm:ss');
      }
      return handledFilter;
    });
    return getPartAttributesFiltersQueryString(filters);
  }

  getPadbFiltersQueryString() {
    return getUserAttributesFiltersQueryString(this.padbAttributesFilters, true);
  }

  getCustomFiltersQueryString() {
    return getUserAttributesFiltersQueryString(this.customAttributesFilters, false);
  }

  getColumnsQueryString(columns: Array<string>): string {
    return encodeURI(`&columns=${JSON.stringify(_.uniq(columns))}`);
  }
}
